<template>
  <form
    :class="formClasses"
    ref="formRef"
    data-component-name="Form"
    data-event-name="click"
    data-event-content="表单"
    data-event-container="true"
  >
    <slot />
  </form>
</template>

<script lang="ts" setup>
import {
  computed,
  provide,
  reactive,
  toRefs,
  watch,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue'
import { debugWarn, isFunction } from '@jdesign-vue/utils'
import { useNamespace } from '@jdesign-vue/hooks'
import { useFormSize } from './hooks'
import { formContextKey } from './constants'
import { formEmits, formProps } from './form'
import { filterFields, useFormLabelWidth } from './utils'

import type { ValidateFieldsError } from 'async-validator'
import type { Arrayable } from '@jdesign-vue/utils'
import type {
  FormContext,
  FormItemContext,
  FormValidateCallback,
  FormValidationResult,
} from './types'
import type { FormItemProp } from './form-item'

const COMPONENT_NAME = 'JdForm'
defineOptions({
  name: COMPONENT_NAME,
})
const props = defineProps(formProps)
const emit = defineEmits(formEmits)

const fields: FormItemContext[] = []

const formSize = useFormSize()
const ns = useNamespace('form')

const formRef = ref<HTMLFormElement>()
// 标签高度统一相关函数
const unifyLabelHeights = () => {
  if (!props.unifyLabelHeight) return

  nextTick(() => {
    const formElement = formRef.value
    if (!formElement) return

    const labels = formElement.querySelectorAll(`.${ns.b()}-item__label`)
    if (!labels.length) return

    let maxHeight = 0
    labels.forEach((label) => {
      const height = label.getBoundingClientRect().height
      maxHeight = Math.max(maxHeight, height)
    })

    labels.forEach((label) => {
      ;(label as HTMLElement).style.height = `${maxHeight}px`
    })
  })
}

const resetLabelHeights = () => {
  const formElement = formRef.value
  if (!formElement) return

  const labels = formElement.querySelectorAll(`.${ns.b()}-item__label`)
  labels.forEach((label) => {
    ;(label as HTMLElement).style.height = ''
  })
}
const formClasses = computed(() => {
  const { labelPosition, inline, fixStarCenter } = props
  return [
    ns.b(),
    // todo: in v2.2.0, we can remove default
    // in fact, remove it doesn't affect the final style
    ns.m(formSize.value || 'default'),
    {
      [ns.m(`label-${labelPosition}`)]: labelPosition,
      [ns.m('inline')]: inline,
      [ns.m('fix-star-center')]: fixStarCenter,
    },
    ns.is('content-adaptive-height', props.contentAdaptiveHeight),
    props.unifyLabelHeight && ns.m('unifyLabelHeight'),
  ]
})

const addField: FormContext['addField'] = (field) => {
  fields.push(field)
}

const removeField: FormContext['removeField'] = (field) => {
  if (field.prop) {
    fields.splice(fields.indexOf(field), 1)
  }
}

const resetFields: FormContext['resetFields'] = (properties = []) => {
  if (!props.model) {
    debugWarn(COMPONENT_NAME, 'model is required for resetFields to work.')
    return
  }
  filterFields(fields, properties).forEach((field) => field.resetField())
}

const clearValidate: FormContext['clearValidate'] = (props = []) => {
  filterFields(fields, props).forEach((field) => field.clearValidate())
}

const isValidatable = computed(() => {
  const hasModel = !!props.model
  if (!hasModel) {
    debugWarn(COMPONENT_NAME, 'model is required for validate to work.')
  }
  return hasModel
})

const obtainValidateFields = (props: Arrayable<FormItemProp>) => {
  if (fields.length === 0) return []

  const filteredFields = filterFields(fields, props)
  if (!filteredFields.length) {
    debugWarn(COMPONENT_NAME, 'please pass correct props!')
    return []
  }
  return filteredFields
}

const validate = async (
  callback?: FormValidateCallback
): FormValidationResult => validateField(undefined, callback)

const doValidateField = async (
  props: Arrayable<FormItemProp> = []
): Promise<boolean> => {
  if (!isValidatable.value) return false

  const fields = obtainValidateFields(props)
  if (fields.length === 0) return true

  let validationErrors: ValidateFieldsError = {}
  for (const field of fields) {
    try {
      await field.validate('')
    } catch (fields) {
      validationErrors = {
        ...validationErrors,
        ...(fields as ValidateFieldsError),
      }
    }
  }

  if (Object.keys(validationErrors).length === 0) return true
  return Promise.reject(validationErrors)
}

const validateField: FormContext['validateField'] = async (
  modelProps = [],
  callback
) => {
  const shouldThrow = !isFunction(callback)
  try {
    const result = await doValidateField(modelProps)
    // When result is false meaning that the fields are not validatable
    if (result === true) {
      callback?.(result)
    }
    return result
  } catch (e) {
    if (e instanceof Error) throw e

    const invalidFields = e as ValidateFieldsError

    if (props.scrollToError) {
      scrollToField(Object.keys(invalidFields)[0])
    }
    callback?.(false, invalidFields)
    return shouldThrow && Promise.reject(invalidFields)
  }
}

const scrollToField = (prop: FormItemProp) => {
  const field = filterFields(fields, prop)[0]
  if (field) {
    field.$el?.scrollIntoView(props.scrollIntoViewOptions)
  }
}

watch(
  () => props.rules,
  () => {
    if (props.validateOnRuleChange) {
      validate().catch((err) => debugWarn(err))
    }
  },
  { deep: true }
)

// 监听unifyLabelHeight属性变化
watch(
  () => props.unifyLabelHeight,
  (newVal) => {
    if (newVal) {
      unifyLabelHeights()
    } else {
      resetLabelHeights()
    }
  }
)

// 组件挂载时执行高度统一
onMounted(() => {
  if (props.unifyLabelHeight) {
    unifyLabelHeights()
  }
  window.addEventListener('resize', handleResize)
})

// 窗口resize时重新计算高度
const handleResize = () => {
  if (props.unifyLabelHeight) {
    unifyLabelHeights()
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

provide(
  formContextKey,
  reactive({
    ...toRefs(props),
    emit,

    resetFields,
    clearValidate,
    validateField,
    addField,
    removeField,

    ...useFormLabelWidth(),
  })
)

// 提供unifyLabelHeight属性给子组件
provide(
  'unifyLabelHeight',
  computed(() => props.unifyLabelHeight)
)

defineExpose({
  /**
   * @description Validate the whole form. Receives a callback or returns `Promise`.
   */
  validate,
  /**
   * @description Validate specified fields.
   */
  validateField,
  /**
   * @description Reset specified fields and remove validation result.
   */
  resetFields,
  /**
   * @description Clear validation message for specified fields.
   */
  clearValidate,
  /**
   * @description Scroll to the specified fields.
   */
  scrollToField,
})
</script>
